<script setup lang="ts">
  import { computed } from "vue";
  import { storeToRefs } from "pinia";
  import { Grid2x2, Grid3x3, LayoutGrid, X } from "lucide-vue-next";
  import SearchInput from "@/components/episto-ui/search-input/SearchInput.vue";
  import { Select } from "@/components/episto-ui/select";
  import { Badge } from "@/components/ui/badge";
  import { Button } from "@/components/ui/button";
  import { useMAssetLibraryStore, type GridDensity, type LibrarySort, type TagsMode } from "@stores/mAssetLibrary";

  const assetLibraryStore = useMAssetLibraryStore();
  const { searchQuery, sortBy, activeTagFilters, tagsMode, density, campaignToken } =
    storeToRefs(assetLibraryStore);
  const { toggleTagFilter, clearTagFilters, setSearchQuery, setSortBy, setTagsMode } = assetLibraryStore;

  const sortOptions = computed(() => [
    ...(campaignToken.value ? [{ value: "relevance", label: "Suggested for this campaign" }] : []),
    { value: "most_recent", label: "Most recent" },
    { value: "most_used", label: "Most used" },
    { value: "a_z", label: "A–Z" },
  ]);

  const tagsModes: { value: TagsMode; label: string }[] = [
    { value: "all", label: "All tags" },
    { value: "any", label: "Any tag" },
  ];

  const densities: { value: GridDensity; label: string; icon: typeof Grid3x3 }[] = [
    { value: "compact", label: "Compact grid", icon: Grid3x3 },
    { value: "comfortable", label: "Comfortable grid", icon: LayoutGrid },
    { value: "large", label: "Large grid", icon: Grid2x2 },
  ];
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex items-center gap-3">
      <SearchInput
        :model-value="searchQuery"
        placeholder="Search by name or tag"
        full-width
        class="flex-1"
        @update:model-value="(value: string) => setSearchQuery(value)" />
      <Select
        :model-value="sortBy"
        :options="sortOptions"
        class="w-56"
        @update:model-value="(value) => setSortBy(value as LibrarySort)" />
      <div class="flex shrink-0 items-center rounded-md border p-0.5">
        <Button
          v-for="option in densities"
          :key="option.value"
          :variant="density === option.value ? 'secondary' : 'ghost'"
          size="icon-sm"
          :title="option.label"
          :aria-label="option.label"
          :aria-pressed="density === option.value"
          @click="density = option.value">
          <component
            :is="option.icon"
            class="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>

    <div
      v-if="activeTagFilters.length"
      class="flex flex-wrap items-center gap-2">
      <div
        v-if="activeTagFilters.length > 1"
        class="flex items-center rounded-md border p-0.5">
        <Button
          v-for="option in tagsModes"
          :key="option.value"
          :variant="tagsMode === option.value ? 'secondary' : 'ghost'"
          size="sm"
          class="h-6 px-2 text-xs"
          :aria-pressed="tagsMode === option.value"
          @click="setTagsMode(option.value)">
          {{ option.label }}
        </Button>
      </div>
      <Badge
        v-for="tag in activeTagFilters"
        :key="tag"
        variant="secondary"
        class="gap-1">
        {{ tag }}
        <Button
          variant="ghost"
          size="icon-sm"
          class="ml-1 size-4 rounded-full"
          :aria-label="`Remove ${tag} filter`"
          @click="toggleTagFilter(tag)">
          <X class="size-3" />
        </Button>
      </Badge>
      <Button
        variant="link"
        size="sm"
        @click="clearTagFilters">
        Clear all
      </Button>
    </div>
  </div>
</template>
